import type {ComponentPropsWithoutRef} from 'react';
import {forwardRef} from 'react';
import {Slot} from '@radix-ui/react-slot';

import {cn} from '@/modules/foundation/shared/tailwind';

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadingSize = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export type HeadingProps = ComponentPropsWithoutRef<'h1'> & {
	/** Heading level. This will translate into any h1-6 level unless `asChild` is defined */
	level?: HeadingLevel;
	/** Changes text sizing
	 * @default 'h1'
	 * add px value preview in doc
	 * @default 'h1' = 60px, 'h2' = 48px, 'h3' = 36px, 'h4' = 30px, 'h5' = 24px, 'h6' = 20px
	 */
	size?: HeadingSize;
	/** Adds margin-bottom */
	spacing?: boolean;
	/**
	 * Change the default rendered element for the one passed as a child, merging their props and behavior.
	 * @default false
	 */
	asChild?: boolean;
};

/** Use `Heading` to render h1-6 elements with heading text styles.  */
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
	({level = 1, size, spacing = false, className, asChild, ...rest}, ref) => {
		const Component = asChild ? Slot : `h${level ?? 1}`;

		let defaultSize: HeadingProps['size'] = 'h1';

		switch (level) {
			case 1:
				defaultSize = 'h1';
				break;
			case 2:
				defaultSize = 'h2';
				break;
			case 3:
				defaultSize = 'h3';
				break;
			case 4:
				defaultSize = 'h4';
				break;
			case 5:
				defaultSize = 'h5';
				break;
			case 6:
				defaultSize = 'h6';
				break;
		}

		size = size ?? defaultSize;

		return (
			<Component
				ref={ref}
				className={cn([
					'font-display',

					size === 'h6' && ['text-lg md:text-xl'],
					size === 'h5' && ['text-xl md:text-2xl'],
					size === 'h4' && ['text-xl md:text-2xl'],
					size === 'h3' && ['text-xl md:text-2xl xl:text-3xl'],
					size === 'h2' && ['text-2xl md:text-4xl xl:text-5xl'],
					size === 'h1' && ['text-4xl md:text-5xl xl:text-6xl'],

					spacing && [
						size === 'h6' && ['mb-3'],
						size === 'h5' && ['mb-3'],
						size === 'h4' && ['mb-4'],
						size === 'h3' && ['mb-4'],
						size === 'h2' && ['mb-6'],
						size === 'h1' && ['mb-8'],
					],
					className,
				])}
				{...rest}
			/>
		);
	},
);

Heading.displayName = 'Heading';
